import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  Camera,
  ClockCounterClockwise,
  Globe,
  SignOut,
  Wrench,
  Backpack,
  FolderOpen,
  Briefcase,
  BookOpen,
  Presentation,
  Lightbulb,
  TextAa,
  Buildings,
  Printer as PrinterIcon,
  Sparkle,
  Sun,
  GraduationCap,
  CaretDown,
  FilmStrip,
  ClipboardText,
  Monitor,
  Plug,
  ChartBar,
  Crosshair,
  PlusMinus,
  Ruler,
  CircleDashed,
  ArrowsHorizontal,
  Target,
  Wrench as WrenchIcon,
  Thermometer,
  FileText,
  Square as SquareIcon,
  DeviceMobile,
  PersonSimple as PersonSimpleIcon,
  Aperture,
  Gauge,
  GridFour,
  Cards,
  CircleHalf,
  Exam,
  Certificate,
  Medal,
} from "@phosphor-icons/react";
import { useQuizBadges } from "@/contexts/QuizBadgesContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OPEN_WHATS_NEW_EVENT } from "@/data/releases";

// Items grouped under the "Learn" dropdown — all educational pages.
const LEARN_ITEMS = [
  { to: "/anatomy", labelKey: "nav_anatomy", Icon: Camera, testId: "nav-learn-anatomy" },
  { to: "/bayer-filter", labelKey: "nav_bayer", Icon: GridFour, testId: "nav-learn-bayer" },
  { to: "/camera-history", labelKey: "nav_camera_history", Icon: ClockCounterClockwise, testId: "nav-learn-camera-history" },
  { to: "/lighting-basics", labelKey: "nav_lighting_basics", Icon: Lightbulb, testId: "nav-learn-lighting-basics" },
  { to: "/lighting", labelKey: "nav_lighting", Icon: Lightbulb, testId: "nav-learn-lighting" },
  { to: "/golden-hour", labelKey: "nav_golden_hour", Icon: Sun, testId: "nav-learn-golden-hour" },
  { to: "/histogram", labelKey: "nav_histogram", Icon: ChartBar, testId: "nav-learn-histogram" },
  { to: "/metering", labelKey: "nav_metering", Icon: Crosshair, testId: "nav-learn-metering" },
  { to: "/exposure-compensation", labelKey: "nav_exposure", Icon: PlusMinus, testId: "nav-learn-exposure" },
  { to: "/crop-factor", labelKey: "nav_cropfactor", Icon: Ruler, testId: "nav-learn-cropfactor" },
  { to: "/filters", labelKey: "nav_filters", Icon: CircleDashed, testId: "nav-learn-filters" },
  { to: "/lens-compression", labelKey: "nav_compression", Icon: ArrowsHorizontal, testId: "nav-learn-compression" },
  { to: "/advanced-focus", labelKey: "nav_focus", Icon: Target, testId: "nav-learn-focus" },
  { to: "/white-balance", labelKey: "nav_whitebalance", Icon: Thermometer, testId: "nav-learn-whitebalance" },
  { to: "/gray-card", labelKey: "nav_graycard", Icon: SquareIcon, testId: "nav-learn-graycard" },
  { to: "/smartphone-photography", labelKey: "nav_smartphone", Icon: DeviceMobile, testId: "nav-learn-smartphone" },
  { to: "/film-photography", labelKey: "nav_film", Icon: FilmStrip, testId: "nav-learn-film" },
  { to: "/black-white", labelKey: "nav_bw", Icon: CircleHalf, testId: "nav-learn-bw" },
  { to: "/prep-checklist", labelKey: "nav_checklist", Icon: ClipboardText, testId: "nav-learn-checklist" },
  { to: "/poses", labelKey: "nav_poses", Icon: PersonSimpleIcon, testId: "nav-learn-poses" },
  { to: "/mode-dial", labelKey: "nav_mode_dial", Icon: Aperture, testId: "nav-learn-mode-dial" },
  { to: "/light-meter", labelKey: "nav_light_meter", Icon: Gauge, testId: "nav-learn-light-meter" },
  { to: "/maintenance", labelKey: "nav_maintenance", Icon: WrenchIcon, testId: "nav-learn-maintenance" },
  { to: "/forms", labelKey: "nav_forms", Icon: FileText, testId: "nav-learn-forms" },
  { to: "/studio", labelKey: "nav_studio", Icon: Buildings, testId: "nav-learn-studio" },
  { to: "/printing", labelKey: "nav_printing", Icon: PrinterIcon, testId: "nav-learn-printing" },
  { to: "/shots", labelKey: "nav_shots", Icon: FilmStrip, testId: "nav-learn-shots" },
  { to: "/calibration", labelKey: "nav_calibration", Icon: Monitor, testId: "nav-learn-calibration" },
  { to: "/tether", labelKey: "nav_tether", Icon: Plug, testId: "nav-learn-tether" },
  { to: "/quizzes", labelKey: "nav_quizzes", Icon: Exam, testId: "nav-learn-quizzes" },
  { to: "/exam", labelKey: "nav_exam", Icon: Certificate, testId: "nav-learn-exam" },
  { to: "/glossary", labelKey: "nav_glossary", Icon: TextAa, testId: "nav-learn-glossary" },
  { to: "/guide", labelKey: "nav_guide", Icon: BookOpen, testId: "nav-learn-guide" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { badges } = useQuizBadges();
  const { lang, toggleLang, t } = useTranslation();
  const navigate = useNavigate();
  const isAuthed = user && user !== false;

  return (
    <nav
      className="sticky top-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 shrink-0 text-white font-semibold text-lg hover:opacity-90 transition-opacity"
          data-testid="nav-logo"
        >
          <Camera weight="fill" className="text-[#F5A524] w-6 h-6" />
          <span className="font-['Outfit'] tracking-tight hidden sm:inline">
            DSLR Advisor
          </span>
        </Link>

        <div className="flex items-center gap-2 min-w-0 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLang}
            className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
            data-testid="lang-toggle"
          >
            <Globe className="w-4 h-4 mr-1.5" />
            {lang.toUpperCase()}
          </Button>

          {/* Logged-out view: minimal — just the language toggle + Sign In CTA */}
          {!isAuthed && user === false && (
            <Link to="/login">
              <Button
                size="sm"
                className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium"
                data-testid="nav-login"
              >
                {t("nav_login")}
              </Button>
            </Link>
          )}

          {/* Logged-in: full product navigation */}
          {isAuthed && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.dispatchEvent(new CustomEvent(OPEN_WHATS_NEW_EVENT))}
                className="text-[#A1A1AA] hover:text-[#F5A524] hover:bg-white/5"
                data-testid="nav-whats-new"
                aria-label={lang === "es" ? "Novedades" : "What's New"}
                title={lang === "es" ? "Novedades" : "What's New"}
              >
                <Sparkle weight="fill" className="w-4 h-4" />
              </Button>

              <Link to="/tools">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  data-testid="nav-tools"
                >
                  <Wrench className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t("nav_tools")}</span>
                </Button>
              </Link>

              <Link to="/projects">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  data-testid="nav-projects"
                >
                  <FolderOpen className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t("nav_projects")}</span>
                </Button>
              </Link>

              <Link to="/careers">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  data-testid="nav-careers"
                >
                  <Briefcase className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t("nav_careers")}</span>
                </Button>
              </Link>

              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
              >
                <Link to="/task-cards" data-testid="nav-task-cards">
                  <Cards className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t("nav_task_cards")}</span>
                </Link>
              </Button>

              {/* Learn dropdown — groups all educational pages */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                    data-testid="nav-learn"
                  >
                    <GraduationCap className="w-4 h-4 mr-1.5" />
                    <span className="hidden sm:inline">{t("nav_learn")}</span>
                    <CaretDown className="w-3 h-3 ml-1 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-[#0F0F12] border-white/10 text-[#D4D4D8] min-w-[220px] max-h-[70vh] overflow-y-auto"
                  data-testid="nav-learn-menu"
                >
                  {LEARN_ITEMS.map(({ to, labelKey, Icon, testId }) => (
                    <DropdownMenuItem
                      key={to}
                      asChild
                      className="cursor-pointer focus:bg-white/5 focus:text-white"
                    >
                      <Link to={to} data-testid={testId} className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[#F5A524]" />
                        {t(labelKey)}
                        {badges.has(to) && (
                          <Medal weight="fill" className="w-3.5 h-3.5 text-emerald-400 ml-auto" data-testid={`${testId}-badge`} />
                        )}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/gear">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  data-testid="nav-gear"
                >
                  <Backpack className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t("nav_gear")}</span>
                </Button>
              </Link>

              <Link to="/history">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  data-testid="nav-history"
                >
                  <ClockCounterClockwise className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t("nav_history")}</span>
                </Button>
              </Link>

              {user.role === "admin" && (
                <Link to="/presenter">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#F5A524] hover:text-[#F5B041] hover:bg-white/5"
                    data-testid="nav-presenter"
                  >
                    <Presentation className="w-4 h-4 mr-1.5" />
                    <span className="hidden md:inline">Presenter</span>
                  </Button>
                </Link>
              )}

              <div className="flex items-center gap-2">
                <span className="text-[#A1A1AA] text-sm hidden sm:inline">
                  {user.name || user.email}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  data-testid="nav-logout"
                >
                  <SignOut className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
